import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { useAudioRecorder } from '../hooks';
import { Button, AudioPlayer, FeedbackModal, PracticeCompleteModal, PracticeTimer, LoadingWhale } from '../components/common';
import { transcribeAudioPro, getSpeakingExercise } from '../services/api';
import { SessionResult } from '../types';

// Fallback exercises based on complexity
const fallbackExercises = {
  basic: [
    'Hello!',
    'My name is...',
    'I like cats.',
    'The sun is yellow.',
    'I have a ball.',
  ],
  intermediate: [
    'The cat is sleeping on the bed.',
    'I like to read books every day.',
    'My favorite color is blue.',
    'The weather is very nice today.',
    'Can you help me please?',
  ],
  advanced: [
    'The beautiful butterfly flew over the garden.',
    'I enjoy learning new things at school.',
    'My grandmother bakes delicious chocolate cookies.',
    'The library has many interesting books to read.',
    'Please remember to bring your homework tomorrow.',
  ],
};

const SpeakingPage: React.FC = () => {
  const navigate = useNavigate();
  const { profile } = useUser();
  const { startSession, endSession, stats, loading: statsLoading } = useUserStats();
  const {
    isRecording,
    audioBlob,
    error: recordingError,
    recordingTime,
    startRecording,
    stopRecording,
    resetRecording,
  } = useAudioRecorder();

  const [exercise, setExercise] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [exerciseCount, setExerciseCount] = useState(0);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [sessionResult, setSessionResult] = useState<SessionResult | null>(null);
  const [lastScore, setLastScore] = useState(0);
  const [isPracticing, setIsPracticing] = useState(true);

  const complexity = profile?.complexity || 'basic';

  // Start session when stats are loaded
  useEffect(() => {
    if (stats && !statsLoading) {
      startSession('speaking');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stats, statsLoading]);

  // Load exercise
  const loadExercise = async () => {
    setIsLoading(true);
    setResult(null);
    resetRecording();

    try {
      const data = await getSpeakingExercise(complexity);
      setExercise(data.text);
    } catch (err) {
      // Use fallback
      const exercises = fallbackExercises[complexity];
      const randomIndex = Math.floor(Math.random() * exercises.length);
      setExercise(exercises[randomIndex]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadExercise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complexity]);

  // Analyze recording when audioBlob changes
  useEffect(() => {
    if (audioBlob && exercise) {
      analyzeRecording();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob]);

  const analyzeRecording = async () => {
    if (!audioBlob || !exercise) return;

    setIsAnalyzing(true);

    try {
      const analysisResult = await transcribeAudioPro(audioBlob, exercise);
      setResult(analysisResult);
      // Calculate and save the score
      const proScore = analysisResult?.proAnalysis?.pronunciationScore || 0;
      setLastScore(proScore);
      setShowFeedback(true);
      setExerciseCount((prev) => prev + 1);
    } catch (err) {
      console.error('Analysis error:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRecordToggle = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleNext = () => {
    setShowFeedback(false);
    loadExercise();
  };

  const handleTryAgain = () => {
    setShowFeedback(false);
    setResult(null);
    resetRecording();
  };

  const handleGoBack = async () => {
    setIsPracticing(false);
    const sessionRes = await endSession(lastScore);
    if (sessionRes) {
      setSessionResult(sessionRes);
      setShowProgressModal(true);
    } else {
      navigate('/app');
    }
  };

  const handleContinuePractice = () => {
    setShowProgressModal(false);
    setSessionResult(null);
    setIsPracticing(true);
    startSession('speaking');
  };

  const handleGoHome = () => {
    setShowProgressModal(false);
    navigate('/app');
  };

  const getScore = () => {
    if (!result?.proAnalysis) return 0;
    return Math.round(result.proAnalysis.accuracy);
  };

  const getFeedbackType = (): 'success' | 'error' => {
    const score = getScore();
    return score >= 70 ? 'success' : 'error';
  };

  const getFeedbackMessage = () => {
    const score = getScore();
    if (score >= 90) return 'Amazing! You said it perfectly!';
    if (score >= 70) return 'Great job! Keep practicing!';
    if (score >= 50) return 'Good try! Listen again and try once more.';
    return "Let's try again! Listen carefully to the example.";
  };

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header - Always rendered to keep timer running */}
      <header className="bg-white dark:bg-slate-800 shadow-md dark:shadow-slate-900 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
          >
            <span className="text-2xl">{'\u2190'}</span>
            <span className="font-bold">Done</span>
          </button>
          <h1 className="text-2xl font-bold text-pink-600 dark:text-pink-400 flex items-center gap-2">
            <span>{'\u{1F399}'}</span> Speaking
          </h1>
          <PracticeTimer isActive={isPracticing} />
        </div>
      </header>

      {/* Loading state */}
      {isLoading ? (
        <LoadingWhale message="Loading exercise..." />
      ) : (
      /* Main content */
      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Exercise counter */}
        <div className="text-center mb-4">
          <span className="text-gray-500 dark:text-gray-400">Exercise {exerciseCount + 1}</span>
        </div>

        {/* Target text */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900 mb-6">
          <p className="text-center text-gray-500 dark:text-gray-400 mb-4">Say this:</p>
          <p className="text-3xl font-bold text-center text-gray-800 dark:text-white mb-6">
            "{exercise}"
          </p>

          {/* Listen button */}
          <div className="flex justify-center">
            <AudioPlayer text={exercise} maxPlays={5} size="medium" />
          </div>
        </div>

        {/* Recording section */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-lg dark:shadow-slate-900">
          {recordingError && (
            <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-4 text-red-700 dark:text-red-400 text-center mb-4">
              {recordingError}
            </div>
          )}

          {/* Recording timer */}
          {isRecording && (
            <div className="text-center mb-4">
              <span className="text-4xl font-bold text-kids-error animate-pulse">
                {recordingTime}s
              </span>
            </div>
          )}

          {/* Record button */}
          <div className="flex justify-center mb-4">
            <button
              onClick={handleRecordToggle}
              disabled={isAnalyzing}
              className={`
                w-32 h-32 rounded-full flex items-center justify-center
                transition-all duration-200
                ${isRecording
                  ? 'bg-kids-error animate-pulse hover:bg-red-600'
                  : 'bg-pink-500 hover:bg-pink-600'
                }
                ${isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}
              `}
            >
              <span className="text-5xl text-white">
                {isRecording ? '\u23F9' : '\u{1F3A4}'}
              </span>
            </button>
          </div>

          <p className="text-center text-gray-600 dark:text-gray-300">
            {isAnalyzing
              ? 'Checking your pronunciation...'
              : isRecording
              ? 'Tap to stop recording'
              : 'Tap the microphone to start speaking'}
          </p>

          {/* Progress indicator when analyzing */}
          {isAnalyzing && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                <div className="bg-pink-500 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          )}
        </div>

        {/* Skip button */}
        <div className="text-center mt-6">
          <Button onClick={handleNext} variant="secondary" size="medium">
            Skip this one
          </Button>
        </div>
      </main>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        type={getFeedbackType()}
        title={getFeedbackType() === 'success' ? 'Great Job!' : 'Try Again!'}
        message={getFeedbackMessage()}
        score={getScore()}
        buttonText="Next Exercise"
        onButtonClick={handleNext}
        onTryAgain={handleTryAgain}
      />

      {/* Practice Complete Modal */}
      <PracticeCompleteModal
        isOpen={showProgressModal}
        onClose={() => setShowProgressModal(false)}
        result={sessionResult}
        onContinue={handleContinuePractice}
        onGoHome={handleGoHome}
      />
    </div>
  );
};

export default SpeakingPage;
